import { useState, useEffect, useRef, useCallback } from 'react';
import GoldIcon from '../images/gold_icon.webp';
import CpIcon from '../images/conquerpoints_icon.webp';
import { ClipboardDataFill, PersonFill, ShieldLockFill, ChatDotsFill,
         ArchiveFill, ArrowLeftRight, ShopWindow,
         ChevronLeft, ChevronRight, Search, XCircleFill } from 'react-bootstrap-icons';
import { AppModal } from '../components/AppModal';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { EmptyState } from '../components/EmptyState';
import { StaffPageHeader } from '../components/StaffPageHeader';
import {
  searchLogAccounts, searchItemTypes, getChatModes,
  getLoginLogs, getChatLogs, getWhisperChannels,
  getItemLogs, getTradeLogs, getTradeItems, getVendingLogs, getVendingActionTypes,
} from '../services/LogService';
import './StaffLogViewer.css';

// ── Constants ──────────────────────────────────────────────────────────────────

const TAB_LOGINS  = 'logins';
const TAB_CHAT    = 'chat';
const TAB_ITEMS   = 'items';
const TAB_TRADES  = 'trades';
const TAB_VENDING = 'vending';

const TABS = [
  { id: TAB_LOGINS,  label: 'Logins',  icon: <ShieldLockFill /> },
  { id: TAB_CHAT,    label: 'Chat',    icon: <ChatDotsFill /> },
  { id: TAB_ITEMS,   label: 'Items',   icon: <ArchiveFill /> },
  { id: TAB_TRADES,  label: 'Trades',  icon: <ArrowLeftRight /> },
  { id: TAB_VENDING, label: 'Vending', icon: <ShopWindow /> },
];

const TRADE_COLS = 4;
const TRADE_ROWS = 3;

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmt(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString();
}

function fmtDate(s) {
  if (!s) return '—';
  return new Date(s).toLocaleString();
}

function VendingCurrencyIcon({ name }) {
  if (name === 'Gold') return <img src={GoldIcon} alt="Gold" className="sl-currency-icon" />;
  if (name === 'CPs')  return <img src={CpIcon}   alt="CPs"  className="sl-currency-icon" />;
  return null;
}

function loginBadgeVariant(actionName) {
  if (actionName === 'LOGIN_SUCCESS') return 'success';
  if (actionName === 'LOGIN_FAILED_PASSWORD' || actionName === 'LOGIN_FAILED_BANNED') return 'error';
  return 'muted';
}

function fmtDateShort(s) {
  if (!s) return '—';
  return new Date(s).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
}

// ── Pagination ─────────────────────────────────────────────────────────────────

function Pagination({ page, pageSize, totalCount, onPage }) {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  if (totalPages <= 1) return null;
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalCount);

  return (
    <div className="sl-pagination">
      <span className="sl-pagination-info">
        {start}–{end} of {fmt(totalCount)}
      </span>
      <div className="sl-pagination-controls">
        <button
          className="sl-page-btn"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          <ChevronLeft />
        </button>
        <span className="sl-page-current">{page} / {totalPages}</span>
        <button
          className="sl-page-btn"
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
        >
          <ChevronRight />
        </button>
      </div>
    </div>
  );
}

// ── Item Type Filter ───────────────────────────────────────────────────────────

function ItemTypeFilter({ value, onChange }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const debounce = useRef(null);

  useEffect(() => {
    if (!value) { setQuery(''); setResults([]); }
  }, [value]);

  function handleInput(e) {
    const q = e.target.value;
    setQuery(q);
    onChange(null);
    if (debounce.current) clearTimeout(debounce.current);
    if (q.trim().length < 2) { setResults([]); setOpen(false); return; }
    debounce.current = setTimeout(async () => {
      try {
        const res = await searchItemTypes(q.trim());
        setResults(res ?? []);
        setOpen(true);
      } catch { setResults([]); }
    }, 300);
  }

  function select(item) {
    onChange(item);
    setQuery(item.name);
    setResults([]);
    setOpen(false);
  }

  function clear() {
    onChange(null);
    setQuery('');
    setResults([]);
    setOpen(false);
  }

  return (
    <div className="sl-itf-root">
      <div className="sl-itf-input-wrap">
        <Search className="sl-itf-icon" />
        <input
          className="sl-itf-input"
          value={query}
          onChange={handleInput}
          placeholder="Filter by item type…"
          autoComplete="off"
        />
        {(query || value) && (
          <button className="sl-itf-clear" onClick={clear} type="button">
            <XCircleFill />
          </button>
        )}
      </div>
      {open && results.length > 0 && (
        <ul className="sl-itf-dropdown">
          {results.map(r => (
            <li key={r.id}>
              <button type="button" onClick={() => select(r)}>
                <span className="sl-itf-name">{r.name}</span>
                <span className="sl-itf-id">#{r.id}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Account Search ─────────────────────────────────────────────────────────────

function LogAccountSearch({ value, onChange }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const debounce = useRef(null);

  useEffect(() => {
    if (!value) { setQuery(''); setResults([]); setSearched(false); }
  }, [value]);

  function handleInput(e) {
    const q = e.target.value;
    setQuery(q);
    setSearched(false);
    setResults([]);
    if (debounce.current) clearTimeout(debounce.current);
    if (q.trim().length < 2) { setSearching(false); return; }
    setSearching(true);
    debounce.current = setTimeout(async () => {
      try {
        const res = await searchLogAccounts(q.trim());
        setResults(res ?? []);
      } catch { setResults([]); }
      finally { setSearching(false); setSearched(true); }
    }, 300);
  }

  function select(a) {
    onChange(a);
    setQuery('');
    setResults([]);
    setSearched(false);
  }

  if (value) {
    return (
      <div className="sl-accs-selected">
        <PersonFill className="sl-accs-icon" />
        <div className="sl-accs-info">
          <span className="sl-accs-charname">{value.characterName || <em>No character</em>}</span>
          <span className="sl-accs-meta">{value.username} · UID {value.id}</span>
        </div>
        <button className="sl-accs-clear" onClick={() => onChange(null)}>×</button>
      </div>
    );
  }

  return (
    <div className="sl-accs-root">
      <div className={`sl-accs-wrap${searching ? ' searching' : ''}`}>
        <Search className="sl-accs-search-icon" />
        <input
          className="sl-accs-input"
          value={query}
          onChange={handleInput}
          placeholder="Search by username, UID, or ingame name…"
          autoComplete="off"
        />
        {searching && <span className="sl-accs-spinner" />}
      </div>
      {results.length > 0 && (
        <ul className="sl-accs-results">
          {results.map(a => (
            <li key={a.id}>
              <button type="button" className="sl-accs-result" onClick={() => select(a)}>
                <span className="sl-accs-r-char">{a.characterName || <em>No character</em>}</span>
                <span className="sl-accs-r-user">{a.username}</span>
                <span className="sl-accs-r-uid">UID {a.id}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {searched && !searching && results.length === 0 && (
        <div className="sl-accs-empty">No accounts found for &ldquo;{query}&rdquo;</div>
      )}
    </div>
  );
}

// ── Logins Tab ─────────────────────────────────────────────────────────────────

function LoginsTab({ userId, from, to }) {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (p) => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    try {
      const result = await getLoginLogs({ userId, from, to, page: p, pageSize: 25 });
      if (!cancelled) setData(result);
    } catch (err) {
      if (!cancelled) setError(err.message);
    } finally {
      if (!cancelled) setLoading(false);
    }
    return () => { cancelled = true; };
  }, [userId, from, to]);

  useEffect(() => { setPage(1); load(1); }, [load]);

  function goPage(p) { setPage(p); load(p); }

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="sl-error">{error}</div>;
  if (!data) return null;
  if (data.items.length === 0) return <EmptyState message="No login events found." />;

  return (
    <div className="sl-tab-content">
      <div className="sl-table-wrap">
        <table className="sl-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>UID</th>
              <th>Username</th>
              <th>Action</th>
              <th>IP Address</th>
              <th>MAC Address</th>
              <th>HWID</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map(e => (
              <tr key={e.id}>
                <td className="sl-td-date">{fmtDate(e.date)}</td>
                <td className="sl-td-mono">{e.userID}</td>
                <td className="sl-td-name">{e.username}</td>
                <td><span className={`sl-badge sl-badge--${loginBadgeVariant(e.actionName)}`}>{e.actionName}</span></td>
                <td className="sl-td-mono">{e.ipAddress}</td>
                <td className="sl-td-mono">{e.macAddress}</td>
                <td className="sl-td-mono sl-hwid">{e.hwid}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination page={page} pageSize={25} totalCount={data.totalCount} onPage={goPage} />
    </div>
  );
}

// ── Chat Tab ───────────────────────────────────────────────────────────────────

function ChatTab({ userId, from, to }) {
  const [modes, setModes] = useState([]);
  const [selectedMode, setSelectedMode] = useState(null);
  const [isWhisperMode, setIsWhisperMode] = useState(false);
  const [data, setData] = useState(null);
  const [whisperChannels, setWhisperChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load modes once
  useEffect(() => {
    let cancelled = false;
    getChatModes().then(m => { if (!cancelled) setModes(m ?? []); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const isWhisper = useCallback((modeId) => {
    const mode = modes.find(m => m.id === modeId);
    return mode ? /whisper|private/i.test(mode.name) : false;
  }, [modes]);

  const load = useCallback(async (modeId, p) => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    setWhisperChannels([]);
    setActiveChannel(null);
    try {
      const whisper = isWhisper(modeId);
      setIsWhisperMode(whisper);
      if (whisper && userId) {
        const channels = await getWhisperChannels({ userId, from, to });
        if (!cancelled) setWhisperChannels(channels ?? []);
      } else {
        const result = await getChatLogs({ userId, chatMode: modeId, from, to, page: p, pageSize: 50 });
        if (!cancelled) setData(result);
      }
    } catch (err) {
      if (!cancelled) setError(err.message);
    } finally {
      if (!cancelled) setLoading(false);
    }
    return () => { cancelled = true; };
  }, [userId, from, to, isWhisper]);

  useEffect(() => { setPage(1); load(selectedMode, 1); }, [load, selectedMode]);

  function goPage(p) { setPage(p); load(selectedMode, p); }

  return (
    <div className="sl-tab-content">
      <div className="sl-chat-filters">
        <select
          className="sl-select"
          value={selectedMode ?? ''}
          onChange={e => { setSelectedMode(e.target.value ? Number(e.target.value) : null); }}
        >
          <option value="">All modes</option>
          {modes.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </div>

      {loading && <LoadingSpinner />}
      {error && <div className="sl-error">{error}</div>}

      {!loading && !error && isWhisperMode && userId && (
        <WhisperView channels={whisperChannels} active={activeChannel} onSelect={setActiveChannel} />
      )}
      {!loading && !error && isWhisperMode && !userId && (
        <div className="sl-info-box">Select a player to view their whisper channels.</div>
      )}
      {!loading && !error && !isWhisperMode && data && (
        <ChatTable data={data} page={page} onPage={goPage} />
      )}
    </div>
  );
}

function WhisperView({ channels, active, onSelect }) {
  if (channels.length === 0) return <EmptyState message="No whisper logs found." />;

  const selected = channels.find(c => c.otherParty === active);

  return (
    <div className="sl-whisper">
      <div className="sl-whisper-list">
        {channels.map(ch => (
          <button
            key={ch.otherParty}
            type="button"
            className={`sl-whisper-chan${ch.otherParty === active ? ' active' : ''}`}
            onClick={() => onSelect(ch.otherParty)}
          >
            <span className="sl-whisper-name">{ch.otherParty}</span>
            <span className="sl-whisper-time">{fmtDateShort(ch.lastMessageDate)}</span>
            <span className="sl-whisper-preview">
              {ch.messages[ch.messages.length - 1]?.content}
            </span>
          </button>
        ))}
      </div>
      <div className="sl-whisper-msgs">
        {selected ? (
          <>
            <div className="sl-whisper-header">
              Messages sent to <strong>{selected.otherParty}</strong>
              <span className="sl-whisper-oneway">(outbound only)</span>
            </div>
            <div className="sl-whisper-feed">
              {selected.messages.map(m => (
                <div key={m.id} className="sl-whisper-msg">
                  <span className="sl-whisper-msg-time">{fmtDate(m.date)}</span>
                  <span className="sl-whisper-msg-content">{m.content}</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="sl-whisper-select-prompt">Select a conversation</div>
        )}
      </div>
    </div>
  );
}

function ViewersModal({ viewers, onHide }) {
  const [query, setQuery] = useState('');
  useEffect(() => { if (!viewers) setQuery(''); }, [viewers]);
  const filtered = viewers
    ? viewers.filter(v => v.toLowerCase().includes(query.toLowerCase()))
    : [];

  return (
    <AppModal show={!!viewers} onHide={onHide} size="lg">
      <AppModal.Header closeButton>
        <AppModal.Title>Viewers ({viewers?.length ?? 0})</AppModal.Title>
      </AppModal.Header>
      <AppModal.Body>
        <input
          className="sl-viewers-search"
          placeholder="Search viewers…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          autoFocus
        />
        {filtered.length === 0
          ? <div className="sl-viewers-empty">No viewers match &ldquo;{query}&rdquo;</div>
          : <div className="sl-viewers-grid">
              {filtered.map((v, i) => <span key={i} className="sl-viewer-pill">{v}</span>)}
            </div>
        }
      </AppModal.Body>
    </AppModal>
  );
}

function ChatTable({ data, page, onPage }) {
  const [viewersOpen, setViewersOpen] = useState(null);

  if (data.items.length === 0) return <EmptyState message="No chat logs found." />;

  return (
    <>
      <div className="sl-table-wrap">
        <table className="sl-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Sender</th>
              <th>Mode</th>
              <th>Target</th>
              <th>Content</th>
              <th>Viewers</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map(e => (
              <tr key={e.id}>
                <td className="sl-td-date">{fmtDate(e.date)}</td>
                <td className="sl-td-name">{e.username}</td>
                <td><span className="sl-badge sl-badge--info">{e.chatModeName}</span></td>
                <td>{e.chatTarget || '—'}</td>
                <td className="sl-td-content">{e.content}</td>
                <td>
                  {e.viewers?.length > 0
                    ? <button type="button" className="sl-viewers-btn" onClick={() => setViewersOpen(e.viewers)}>
                        {e.viewers.length}
                      </button>
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination page={page} pageSize={50} totalCount={data.totalCount} onPage={onPage} />
      <ViewersModal viewers={viewersOpen} onHide={() => setViewersOpen(null)} />
    </>
  );
}

// ── Items Tab ──────────────────────────────────────────────────────────────────

function ItemsTab({ userId, from, to }) {
  const [itemType, setItemType] = useState(null);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (p, it) => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    try {
      const result = await getItemLogs({ userId, from, to, itemType: it?.id, page: p, pageSize: 25 });
      if (!cancelled) setData(result);
    } catch (err) {
      if (!cancelled) setError(err.message);
    } finally {
      if (!cancelled) setLoading(false);
    }
    return () => { cancelled = true; };
  }, [userId, from, to]);

  useEffect(() => { setPage(1); load(1, itemType); }, [load, itemType]);

  function goPage(p) { setPage(p); load(p, itemType); }

  function handleItemType(it) { setItemType(it); }

  return (
    <div className="sl-tab-content">
      <div className="sl-filters-row">
        <ItemTypeFilter value={itemType} onChange={handleItemType} />
      </div>
      {loading && <LoadingSpinner />}
      {error && <div className="sl-error">{error}</div>}
      {!loading && !error && data && (
        data.items.length === 0
          ? <EmptyState message="No item events found." />
          : <>
            <div className="sl-table-wrap">
              <table className="sl-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Player</th>
                    <th>Action</th>
                    <th>Item</th>
                    <th>Type #</th>
                    <th>Qty</th>
                    <th>Location</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map(e => (
                    <tr key={e.id}>
                      <td className="sl-td-date">{fmtDate(e.date)}</td>
                      <td className="sl-td-name">{e.username}</td>
                      <td><span className="sl-badge sl-badge--neutral">{e.actionName}</span></td>
                      <td>{e.itemTypeName}</td>
                      <td className="sl-td-mono">#{e.itemType}</td>
                      <td>{e.itemQuantity ?? '—'}</td>
                      <td className="sl-td-mono">{e.map != null ? `${e.map} (${e.x},${e.y})` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} pageSize={25} totalCount={data.totalCount} onPage={goPage} />
          </>
      )}
    </div>
  );
}

// ── Trade modal (loaded on demand) ────────────────────────────────────────────

function TradeItemSlot({ item }) {
  const [showModal, setShowModal] = useState(false);
  if (!item) return <div className="sl-trade-slot sl-trade-slot--empty" />;

  return (
    <>
      <button
        type="button"
        className="sl-trade-slot sl-trade-slot--filled"
        onClick={() => setShowModal(true)}
        title={item.itemTypeName}
      >
        <span className="sl-trade-slot-label">{item.itemTypeName.substring(0, 6)}</span>
        {item.plus > 0 && <span className="sl-trade-slot-plus">+{item.plus}</span>}
      </button>

      <AppModal show={showModal} onHide={() => setShowModal(false)} size="sm">
        <AppModal.Header closeButton>
          <AppModal.Title>{item.itemTypeName}</AppModal.Title>
        </AppModal.Header>
        <AppModal.Body>
          <div className="sl-item-detail">
            <div className="sl-item-detail-row"><span>Type #</span><span>#{item.itemType}</span></div>
            {item.plus > 0 && <div className="sl-item-detail-row"><span>Plus</span><span>+{item.plus}</span></div>}
            {item.socketOne > 0 && <div className="sl-item-detail-row"><span>Socket 1</span><span>{item.socketOne}</span></div>}
            {item.socketTwo > 0 && <div className="sl-item-detail-row"><span>Socket 2</span><span>{item.socketTwo}</span></div>}
            {item.effect > 0 && <div className="sl-item-detail-row"><span>Effect</span><span>{item.effect}</span></div>}
            {item.bless > 0 && <div className="sl-item-detail-row"><span>Bless</span><span>{item.bless}</span></div>}
            {item.enchant > 0 && <div className="sl-item-detail-row"><span>Enchant</span><span>{item.enchant}</span></div>}
            {item.color > 0 && <div className="sl-item-detail-row"><span>Color</span><span>{item.color}</span></div>}
            {item.stackSize > 1 && <div className="sl-item-detail-row"><span>Stack</span><span>{item.stackSize}</span></div>}
            {item.perfectionRank > 0 && (
              <div className="sl-item-detail-row">
                <span>Perfection</span>
                <span>Rank {item.perfectionRank} Lv {item.perfectionLevel}</span>
              </div>
            )}
            {item.locked > 0 && <div className="sl-item-detail-row sl-item-detail-row--warn"><span>Bound</span><span>Yes</span></div>}
            {item.ownerName && <div className="sl-item-detail-row"><span>Owner</span><span>{item.ownerName}</span></div>}
            {item.signature && <div className="sl-item-detail-row"><span>Signature</span><span>{item.signature}</span></div>}
          </div>
        </AppModal.Body>
      </AppModal>
    </>
  );
}

function TradePanel({ playerName, items, gold, cp }) {
  const slots = Array.from({ length: TRADE_COLS * TRADE_ROWS }, (_, i) => items[i] ?? null);
  return (
    <div className="sl-trade-panel">
      <div className="sl-trade-panel-header">{playerName}</div>
      <div className="sl-trade-grid">
        {slots.map((item, i) => <TradeItemSlot key={i} item={item} />)}
      </div>
      <div className="sl-trade-currency">
        <div className="sl-trade-currency-row">
          <span className="sl-trade-currency-label">Gold</span>
          <span className="sl-trade-currency-val">{fmt(gold)}</span>
        </div>
        <div className="sl-trade-currency-row">
          <span className="sl-trade-currency-label">CPs</span>
          <span className="sl-trade-currency-val">{fmt(cp)}</span>
        </div>
      </div>
    </div>
  );
}

function TradeDetailModal({ trade, onHide }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!trade) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setItems(null);
    getTradeItems(trade.id, trade.userID).then(result => {
      if (!cancelled) setItems(result);
    }).catch(err => {
      if (!cancelled) setError(err.message);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [trade]);

  return (
    <AppModal show={!!trade} onHide={onHide} size="lg">
      <AppModal.Header closeButton>
        <AppModal.Title>
          Trade #{trade.id} &mdash; {trade.name} ⇄ {trade.targetName}
        </AppModal.Title>
      </AppModal.Header>
      <AppModal.Body>
        {loading && <LoadingSpinner />}
        {error && <div className="sl-error">{error}</div>}
        {!loading && !error && items && (
          <div className="sl-trade-panels">
            <TradePanel
              playerName={trade.name}
              items={items.initiatorItems}
              gold={trade.goldToTarget}
              cp={trade.cpToTarget}
            />
            <div className="sl-trade-arrow">⇄</div>
            <TradePanel
              playerName={trade.targetName}
              items={items.targetItems}
              gold={trade.goldFromTarget}
              cp={trade.cpFromTarget}
            />
          </div>
        )}
      </AppModal.Body>
    </AppModal>
  );
}

// ── Trades Tab ─────────────────────────────────────────────────────────────────

function TradesTab({ userId, from, to }) {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTrade, setSelectedTrade] = useState(null);

  const load = useCallback(async (p) => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    try {
      const result = await getTradeLogs({ userId, from, to, page: p, pageSize: 10 });
      if (!cancelled) setData(result);
    } catch (err) {
      if (!cancelled) setError(err.message);
    } finally {
      if (!cancelled) setLoading(false);
    }
    return () => { cancelled = true; };
  }, [userId, from, to]);

  useEffect(() => { setPage(1); load(1); }, [load]);

  function goPage(p) { setPage(p); load(p); }

  return (
    <div className="sl-tab-content">
      {loading && <LoadingSpinner />}
      {error && <div className="sl-error">{error}</div>}
      {!loading && !error && data && (
        data.items.length === 0
          ? <EmptyState message="No trade records found." />
          : <>
            <div className="sl-table-wrap">
              <table className="sl-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Initiator</th>
                    <th></th>
                    <th>Target</th>
                    <th>Location</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map(t => (
                    <tr key={t.id} className="sl-trade-row">
                      <td className="sl-td-date">{fmtDate(t.date)}</td>
                      <td className="sl-td-name">{t.name}</td>
                      <td className="sl-td-arrow">⇄</td>
                      <td className="sl-td-name">{t.targetName}</td>
                      <td className="sl-td-mono">{t.map != null ? `${t.map} (${t.x},${t.y})` : '—'}</td>
                      <td>
                        <button
                          type="button"
                          className="sl-view-btn"
                          onClick={() => setSelectedTrade(t)}
                        >
                          View Items
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} pageSize={10} totalCount={data.totalCount} onPage={goPage} />
          </>
      )}

      {selectedTrade && <TradeDetailModal trade={selectedTrade} onHide={() => setSelectedTrade(null)} />}
    </div>
  );
}

// ── Vending Tab ────────────────────────────────────────────────────────────────

function VendingTab({ userId, from, to }) {
  const [itemType, setItemType] = useState(null);
  const [actionTypes, setActionTypes] = useState([]);
  const [selectedActions, setSelectedActions] = useState(null);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getVendingActionTypes().then(types => {
      if (!cancelled) {
        setActionTypes(types ?? []);
        setSelectedActions(new Set((types ?? []).filter(t => t !== 'ADD_VEND_ITEM')));
      }
    }).catch(() => {
      if (!cancelled) setSelectedActions(new Set());
    });
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async (p, it, sa) => {
    if (sa === null) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    try {
      const atFilter = sa.size > 0 ? [...sa] : null;
      const result = await getVendingLogs({ userId, from, to, itemType: it?.id, actionTypes: atFilter, page: p, pageSize: 10 });
      if (!cancelled) setData(result);
    } catch (err) {
      if (!cancelled) setError(err.message);
    } finally {
      if (!cancelled) setLoading(false);
    }
    return () => { cancelled = true; };
  }, [userId, from, to]);

  useEffect(() => {
    if (selectedActions === null) return;
    setPage(1);
    load(1, itemType, selectedActions);
  }, [load, itemType, selectedActions]);

  function goPage(p) { setPage(p); load(p, itemType, selectedActions); }

  function toggleAction(name) {
    setSelectedActions(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  }

  return (
    <div className="sl-tab-content">
      <div className="sl-filters-row">
        <ItemTypeFilter value={itemType} onChange={(it) => setItemType(it)} />
        {actionTypes.length > 0 && (
          <div className="sl-action-filter">
            {actionTypes.map(t => (
              <label key={t} className="sl-action-check">
                <input
                  type="checkbox"
                  checked={selectedActions?.has(t) ?? false}
                  onChange={() => toggleAction(t)}
                />
                <span>{t}</span>
              </label>
            ))}
          </div>
        )}
      </div>
      {loading && <LoadingSpinner />}
      {error && <div className="sl-error">{error}</div>}
      {!loading && !error && data && (
        data.items.length === 0
          ? <EmptyState message="No vending events found." />
          : <>
            <div className="sl-table-wrap">
              <table className="sl-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Player</th>
                    <th>Action</th>
                    <th>Item</th>
                    <th>Type #</th>
                    <th>Qty</th>
                    <th>Seller</th>
                    <th>Price</th>
                    <th>Location</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map(e => (
                    <tr key={e.id}>
                      <td className="sl-td-date">{fmtDate(e.date)}</td>
                      <td className="sl-td-name">{e.username}</td>
                      <td><span className="sl-badge sl-badge--neutral">{e.actionName}</span></td>
                      <td>{e.itemTypeName}</td>
                      <td className="sl-td-mono">#{e.itemType}</td>
                      <td>{e.itemQuantity ?? '—'}</td>
                      <td className="sl-td-name">{e.seller || '—'}</td>
                      <td className="sl-td-price">
                        {e.price != null
                          ? <span className="sl-price-with-icon"><VendingCurrencyIcon name={e.currencyName} />{fmt(e.price)}</span>
                          : '—'}
                      </td>
                      <td className="sl-td-mono">{e.map != null ? `${e.map} (${e.x},${e.y})` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} pageSize={10} totalCount={data.totalCount} onPage={goPage} />
          </>
      )}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

function sevenDaysAgo() {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 16);
}

export function StaffLogViewer() {
  const [player, setPlayer]   = useState(null);
  const [from, setFrom]       = useState(sevenDaysAgo);
  const [to, setTo]           = useState('');
  const [activeTab, setActiveTab] = useState(TAB_LOGINS);

  const userId = player?.id ?? null;
  const fromParam = from || null;
  const toParam   = to   || null;

  function clearFilters() {
    setPlayer(null);
    setFrom(sevenDaysAgo());
    setTo('');
  }

  return (
    <div className="sl-page">
      <StaffPageHeader icon={<ClipboardDataFill />} title="Player Logs" description="Search and browse game event logs." />

      {/* Search bar */}
      <div className="sl-search-bar">
        <div className="sl-search-accs">
          <LogAccountSearch value={player} onChange={setPlayer} />
        </div>
        <div className="sl-search-dates">
          <label className="sl-date-label">From</label>
          <input
            type="datetime-local"
            className="sl-date-input"
            value={from}
            onChange={e => setFrom(e.target.value)}
          />
          <label className="sl-date-label">To</label>
          <input
            type="datetime-local"
            className="sl-date-input"
            value={to}
            onChange={e => setTo(e.target.value)}
          />
          {(player || from || to) && (
            <button className="sl-clear-btn" type="button" onClick={clearFilters}>
              <XCircleFill /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Global browse notice */}
      {!player && (
        <div className="sl-global-notice">
          Browsing all players — results are paginated. Select a player above to scope the view.
        </div>
      )}

      {/* Tabs */}
      <div className="sl-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            type="button"
            className={`sl-tab-btn${activeTab === tab.id ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === TAB_LOGINS  && <LoginsTab  userId={userId} from={fromParam} to={toParam} />}
      {activeTab === TAB_CHAT    && <ChatTab    userId={userId} from={fromParam} to={toParam} />}
      {activeTab === TAB_ITEMS   && <ItemsTab   userId={userId} from={fromParam} to={toParam} />}
      {activeTab === TAB_TRADES  && <TradesTab  userId={userId} from={fromParam} to={toParam} />}
      {activeTab === TAB_VENDING && <VendingTab userId={userId} from={fromParam} to={toParam} />}
    </div>
  );
}
